import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Modal, Thumb } from '../components/ui';
import { LibraryForm } from './Exercises';
import { WEEKDAYS } from '../lib/activity';
import { downloadProgramExport } from '../lib/programExport';

const ICONS = ['🏋️', '💪', '🦵', '🦾', '🏠', '🔥', '⚡', '🏃', '🤸', '🎯', '🧘', '🥊'];
const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#64748b'];

function useRefresh() {
  const qc = useQueryClient();
  return () => ['programs', 'program', 'exercises', 'library'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
}

function ProgramForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState({ name: '', icon: '🏋️', color: '#10b981', description: '', ...initial });
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const refresh = useRefresh();

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name: form.name, icon: form.icon, color: form.color, description: form.description || '' };
      const saved = initial?.id ? await api.updateProgram(initial.id, body) : await api.createProgram(body);
      refresh();
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <form onSubmit={save} className="form-grid">
      <div className="form-group span-2">
        <label className="form-label">Nom du programme *</label>
        <input className="form-input" value={form.name} onChange={(e) => set('name', e.target.value)} required maxLength={60} autoFocus />
      </div>
      <div className="form-group span-2">
        <label className="form-label">Icône</label>
        <div className="swatches">
          {ICONS.map((i) => (
            <button type="button" key={i} className={'swatch' + (form.icon === i ? ' active' : '')} onClick={() => set('icon', i)}>{i}</button>
          ))}
        </div>
      </div>
      <div className="form-group span-2">
        <label className="form-label">Couleur</label>
        <div className="swatches">
          {COLORS.map((c) => (
            <button type="button" key={c} className={'swatch' + (form.color === c ? ' active' : '')} style={{ background: c }} onClick={() => set('color', c)} aria-label={c} />
          ))}
        </div>
      </div>
      <div className="form-group span-2">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={2} value={form.description || ''} onChange={(e) => set('description', e.target.value)} maxLength={300} />
      </div>
      {error && <div className="alert-error span-2">{error}</div>}
      <div className="form-actions span-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
        <button className="btn btn-primary">Enregistrer</button>
      </div>
    </form>
  );
}

function PlanningModal({ programs, onClose }) {
  const qc = useQueryClient();
  const { data: schedule = [] } = useQuery({ queryKey: ['schedule'], queryFn: api.getSchedule });
  const [days, setDays] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const current = days || Object.fromEntries(schedule.map((p) => [p.weekday, p.program_id]));

  const save = async () => {
    setSaving(true);
    try {
      await api.saveSchedule(current);
      qc.invalidateQueries({ queryKey: ['schedule'] });
      onClose();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Modal title="📅 Planning de la semaine" onClose={onClose}>
      <p className="muted small" style={{ marginBottom: 12 }}>Associe un programme à chaque jour : il apparaît en tête du tableau de bord.</p>
      {WEEKDAYS.map((name, i) => (
        <div className="form-group planning-row" key={name}>
          <label className="form-label" htmlFor={`plan-${i}`}>{name}</label>
          <select
            id={`plan-${i}`}
            className="form-select"
            value={current[i] ?? ''}
            onChange={(e) => setDays({ ...current, [i]: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">Repos</option>
            {programs.map((p) => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
          </select>
        </div>
      ))}
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-primary" onClick={save} disabled={saving} style={{ width: '100%', marginTop: 8 }}>Enregistrer</button>
    </Modal>
  );
}

export default function Programs() {
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: programs = [], isLoading } = useQuery({ queryKey: ['programs'], queryFn: api.getPrograms });
  const [creating, setCreating] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [planning, setPlanning] = useState(() => searchParams.get('planning') === '1');

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement…</div>;

  const duplicate = async (p) => {
    try {
      await api.duplicateProgram(p.id);
      refresh();
    } catch (e) { alert(e.message); }
  };

  const toggleFavorite = async (p) => {
    try {
      await api.setProgramFavorite(p.id, !p.favorite);
      refresh();
    } catch (e) { alert(e.message); }
  };
  const exportAll = async () => {
    setExporting(true);
    try {
      const details = await Promise.all(programs.map((p) => api.getProgram(p.id)));
      downloadProgramExport(details);
    } catch (e) {
      alert(`Impossible d'exporter les programmes : ${e.message}`);
    } finally {
      setExporting(false);
    }
  };
  const favoriteCount = programs.filter((p) => p.favorite).length;
  const visible = onlyFavorites ? programs.filter((p) => p.favorite) : programs;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Programmes</h1>
          <p className="page-subtitle">Crée, modifie et organise tes séances.</p>
        </div>
        <div className="head-actions">
          <button className="btn btn-ghost" onClick={() => setPlanning(true)}>📅 Planning</button>
          <button className="btn btn-secondary" onClick={exportAll} disabled={exporting || programs.length === 0}>
            {exporting ? '⏳ Export…' : '⇩ Exporter les programmes'}
          </button>
          <button className="btn btn-primary" onClick={() => setCreating(true)}>＋ Nouveau programme</button>
        </div>
      </div>
      {planning && <PlanningModal programs={programs} onClose={() => { setPlanning(false); if (searchParams.has('planning')) setSearchParams({}, { replace: true }); }} />}

      {programs.length === 0 && <div className="empty-state">Aucun programme. Crée ton premier programme !</div>}
      {programs.length > 0 && (
        <div className="chips" style={{ marginBottom: 14 }}>
          <button className={'chip chip-filter' + (!onlyFavorites ? ' active' : '')} onClick={() => setOnlyFavorites(false)}>Tous ({programs.length})</button>
          <button className={'chip chip-filter' + (onlyFavorites ? ' active' : '')} onClick={() => setOnlyFavorites(true)}>⭐ Favoris ({favoriteCount})</button>
        </div>
      )}
      {onlyFavorites && visible.length === 0 && <div className="empty-state">Aucun favori. Touche l'étoile d'un programme pour l'ajouter.</div>}
      <div className="grid grid-2">
        {visible.map((p) => (
          <div className="card program-card" key={p.id} style={{ '--pc': p.color }}>
            <div className="card-header">
              <div className="card-title"><span className="program-icon">{p.icon}</span> {p.name}</div>
              <button
                className={'fav-btn' + (p.favorite ? ' active' : '')}
                onClick={() => toggleFavorite(p)}
                title={p.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                aria-label={p.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                aria-pressed={!!p.favorite}
              >{p.favorite ? '★' : '☆'}</button>
            </div>
            {p.description && <p className="muted small">{p.description}</p>}
            <div className="chips">
              <span className="chip">{p.exercise_count} exercices</span>
              <span className="chip">{p.total_sets} séries</span>
                            <span className="chip">{p.session_count} séances</span>
            </div>
            <div className="card-actions">
              <Link className="btn btn-primary btn-sm" to={`/workout?day=${encodeURIComponent(p.name)}&autostart=1`}>▶ Démarrer</Link>
              <Link className="btn btn-secondary btn-sm" to={`/programs/${p.id}`}>✏️ Modifier</Link>
              <button className="btn btn-ghost btn-sm" onClick={() => duplicate(p)}>⧉ Dupliquer</button>
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <Modal title="Nouveau programme" onClose={() => setCreating(false)}>
          <ProgramForm onCancel={() => setCreating(false)} onSaved={(p) => { setCreating(false); navigate(`/programs/${p.id}`); }} />
        </Modal>
      )}
    </div>
  );
}

function NumField({ value, onCommit, min = 0, max, step = 1, width = 64 }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  return (
    <input
      type="number"
      className="form-input mini-input"
      style={{ width }}
      min={min}
      max={max}
      step={step}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        const n = v === '' ? null : parseInt(v, 10);
        if (n !== (value ?? null)) onCommit(n);
      }}
    />
  );
}

function ExerciseEditor({ ex, onChange, onRemove, canMoveUp, canMoveDown, onMove }) {
  const [notes, setNotes] = useState(ex.notes || '');
  useEffect(() => setNotes(ex.notes || ''), [ex.notes]);
  return (
    <div className="ex-edit">
      <div className="ex-edit-head">
        <Thumb src={ex.image_url} alt={ex.name} size={52} />
        <div className="ex-edit-title">
          <div className="ex-edit-name">{ex.name}</div>
          <div className="muted small">{ex.lib_category || ''}{ex.equipment ? ` · ${ex.equipment}` : ''}</div>
        </div>
        {(
          <div className="move-btns">
            <button className="icon-btn" disabled={!canMoveUp} onClick={() => onMove(-1)} aria-label="Monter">▲</button>
            <button className="icon-btn" disabled={!canMoveDown} onClick={() => onMove(1)} aria-label="Descendre">▼</button>
          </div>
        )}
      </div>
      <div className="ex-edit-fields">
        <label>Séries<NumField value={ex.target_sets} min={1} max={20} onCommit={(n) => onChange(ex, { target_sets: n || 1 })} /></label>
        <label>{ex.is_timed ? 'Sec. min' : 'Reps min'}<NumField value={ex.target_reps_min} onCommit={(n) => onChange(ex, { target_reps_min: n })} /></label>
        <label>{ex.is_timed ? 'Sec. max' : 'Reps max'}<NumField value={ex.target_reps_max} onCommit={(n) => onChange(ex, { target_reps_max: n })} /></label>
        <label>Repos (s)<NumField value={ex.rest_seconds} step={5} width={76} onCommit={(n) => onChange(ex, { rest_seconds: n ?? 0 })} /></label>
        <label className="grow">Notes
          <input
            className="form-input mini-input"
            value={notes}
            maxLength={300}
            placeholder="Tempo, drop set, consigne…"
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (ex.notes || '') && onChange(ex, { notes: notes || null })}
          />
        </label>
      </div>
      <div className="ex-edit-actions">
        <button className="btn btn-ghost btn-sm danger-text" onClick={() => onRemove(ex)}>🗑 Retirer</button>
      </div>
    </div>
  );
}

function LibraryPicker({ onPick, onClose, title, excludeIds = [] }) {
  const { data: library = [] } = useQuery({ queryKey: ['library'], queryFn: api.getLibrary });
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return library.filter((l) => !excludeIds.includes(l.id) && (!q || `${l.name} ${l.category} ${l.equipment || ''}`.toLowerCase().includes(q))).slice(0, 80);
  }, [library, search, excludeIds]);

  if (creating) {
    return (
      <Modal title="Créer un exercice" onClose={() => setCreating(false)} wide>
        <LibraryForm initial={{ name: search }} onCancel={() => setCreating(false)} onSaved={(saved) => onPick(saved)} />
      </Modal>
    );
  }
  return (
    <Modal title={title} onClose={onClose} wide>
      <input className="form-input" placeholder="🔎 Rechercher dans la bibliothèque…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
      <div className="picker-list">
        {list.map((l) => (
          <button key={l.id} className="picker-item" onClick={() => onPick(l)}>
            <Thumb src={l.image_url} alt={l.name} size={44} />
            <span><strong>{l.name}</strong><br /><span className="muted small">{l.category}{l.equipment ? ` · ${l.equipment}` : ''}</span></span>
          </button>
        ))}
        {list.length === 0 && <div className="empty-state">Aucun résultat.</div>}
      </div>
      <button className="btn btn-secondary" onClick={() => setCreating(true)}>＋ Créer un nouvel exercice{search ? ` « ${search} »` : ''}</button>
    </Modal>
  );
}

export function ProgramEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: program, isLoading, error } = useQuery({ queryKey: ['program', id], queryFn: () => api.getProgram(id) });
  const [editInfo, setEditInfo] = useState(false);
  const [picker, setPicker] = useState(null);

  if (isLoading) return <div className="loading"><div className="spinner" />Chargement…</div>;
  if (error || !program) return <div className="empty-state">Programme introuvable. <Link to="/programs">Retour</Link></div>;

  const mains = program.exercises;
  const run = async (fn) => {
    try { await fn(); refresh(); } catch (e) { alert(e.message); }
  };
  const exportProgram = () => {
    try {
      downloadProgramExport([program]);
    } catch (e) {
      alert(`Impossible d'exporter ce programme : ${e.message}`);
    }
  };

  const fullPayload = (ex, changes = {}) => ({
    day: program.name,
    library_id: ex.library_id,
    day_order: ex.day_order,
    target_sets: ex.target_sets,
    target_reps_min: ex.target_reps_min,
    target_reps_max: ex.target_reps_max,
    rest_seconds: ex.rest_seconds,
    notes: ex.notes,
    is_timed: !!ex.is_timed,
    ...changes,
  });

  const change = (ex, changes) => run(() => api.updateExercise(ex.id, fullPayload(ex, changes)));
  const remove = (ex) => {
    if (!window.confirm(`Retirer « ${ex.name} » du programme ?`)) return;
    run(() => api.deleteExercise(ex.id));
  };
  const move = (index, dir) => {
    const ids = mains.map((m) => m.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + dir, 0, moved);
    run(() => api.reorderExercises(ids));
  };
  const pick = (lib) => {
    setPicker(null);
    run(() => api.createExercise({
      day: program.name,
      library_id: lib.id,
      day_order: mains.length + 1,
      target_sets: 3,
      target_reps_min: 8,
      target_reps_max: 12,
    }));
  };

  const removeProgram = async () => {
    if (!window.confirm(`Supprimer le programme « ${program.name} » ? Les séances passées restent dans l'historique.`)) return;
    try {
      await api.deleteProgram(program.id);
      refresh();
      navigate('/programs');
    } catch (e) { alert(e.message); }
  };

  const usedIds = mains.map((e) => e.library_id);

  return (
    <div>
      <Link to="/programs" className="back-link">← Programmes</Link>
      <div className="page-head">
        <div>
          <h1 className="page-title">{program.icon} {program.name}</h1>
          {program.description && <p className="page-subtitle">{program.description}</p>}
        </div>
        <div className="head-actions">
          <button className="btn btn-secondary btn-sm" onClick={exportProgram}>⇩ Exporter</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditInfo(true)}>✏️ Infos</button>
          <Link className="btn btn-primary btn-sm" to={`/workout?day=${encodeURIComponent(program.name)}&autostart=1`}>▶ Démarrer</Link>
        </div>
      </div>

      <div className="ex-edit-list">
        {mains.map((m, i) => (
          <div className="ex-edit-group" key={m.id}>
            <ExerciseEditor
              ex={m}
              onChange={change}
              onRemove={remove}
              canMoveUp={i > 0}
              canMoveDown={i < mains.length - 1}
              onMove={(dir) => move(i, dir)}
            />
          </div>
        ))}
        {mains.length === 0 && <div className="empty-state">Ce programme est vide. Ajoute ton premier exercice.</div>}
      </div>

      <div className="form-actions" style={{ justifyContent: 'space-between' }}>
        <button className="btn btn-danger btn-sm" onClick={removeProgram}>🗑 Supprimer le programme</button>
        <button className="btn btn-primary" onClick={() => setPicker({ main: null })}>＋ Ajouter un exercice</button>
      </div>

      {picker && (
        <LibraryPicker
          title="Ajouter un exercice"
          excludeIds={usedIds}
          onClose={() => setPicker(null)}
          onPick={pick}
        />
      )}
      {editInfo && (
        <Modal title="Infos du programme" onClose={() => setEditInfo(false)}>
          <ProgramForm
            initial={program}
            onCancel={() => setEditInfo(false)}
            onSaved={(p) => { setEditInfo(false); if (p.id !== program.id) navigate(`/programs/${p.id}`); }}
          />
        </Modal>
      )}
    </div>
  );
}
